import React, { useState, useMemo } from 'react';
import Pagination from '../common/Pagination/Pagination';

// ─── Forward Cover Sample Data ──────────────────────────────────────────────
// As requested, using the sample data matching the reference image.
const SAMPLE_FORWARD_COVERS = [
    { id: 1, fileId: 's', bgNumber: '12345', usd: 90000, date: '11/03/2026', rate: 78, status: 'INACTIVE' },
    { id: 2, fileId: '2kt', bgNumber: '-', usd: '', date: 'Invalid date', rate: '', status: 'ACTIVE' },
    { id: 3, fileId: 'KL1', bgNumber: 'BG-SAME-1023', usd: 29115.38, date: '30/04/2025', rate: 86.08, status: 'INACTIVE' },
    { id: 4, fileId: 'KL1', bgNumber: 'BG-SAME-1023', usd: 29115.38, date: '30/04/2025', rate: 86.08, status: 'INACTIVE' },
    { id: 5, fileId: 'TEST18', bgNumber: '0042BG000692024', usd: '', date: '05/06/2025', rate: '', status: 'ACTIVE' },
    { id: 6, fileId: 'F3 / 2526', bgNumber: '0042BG000152025', usd: 29115.38, date: '24/10/2025', rate: 86.08, status: 'INACTIVE' },
    { id: 7, fileId: 'F4 / 2526', bgNumber: '0042BG000162025', usd: 23774.26, date: '18/11/2025', rate: 85.94, status: 'INACTIVE' },
    { id: 8, fileId: 'F6 / 2526', bgNumber: '0042BG000182025', usd: 34822.46, date: '15/03/2026', rate: '', status: 'ACTIVE' },
    { id: 9, fileId: '', bgNumber: '0042BG000012026', usd: '', date: 'Invalid date', rate: '', status: 'ACTIVE' }
];

const ITEMS_PER_PAGE = 8;

const ForwardCover = () => {
    // ─── Local State ──────────────────────────────────────────────────────────
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // ─── Filter Logic ──────────────────────────────────────────────────────────
    const filteredCovers = useMemo(() => {
        setCurrentPage(1); // Reset to page 1 on filter/search change
        return SAMPLE_FORWARD_COVERS.filter((item) => {
            const matchesSearch =
                (item.fileId && item.fileId.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (item.bgNumber && item.bgNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
                item.id.toString() === searchTerm;

            const matchesStatus =
                selectedStatus === '' ||
                item.status.toLowerCase() === selectedStatus.toLowerCase();

            return matchesSearch && matchesStatus;
        });
    }, [searchTerm, selectedStatus]);

    // ─── Totals Calculation ──────────────────────────────────────────────────
    const totalUsd = useMemo(() => {
        return filteredCovers.reduce((acc, curr) => {
            const val = Number(curr.usd);
            return acc + (isNaN(val) ? 0 : val);
        }, 0);
    }, [filteredCovers]);

    // ─── Pagination Logic ──────────────────────────────────────────────────────
    const totalPages = Math.ceil(filteredCovers.length / ITEMS_PER_PAGE);

    const paginatedCovers = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredCovers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredCovers, currentPage]);

    // ─── Formatting Helpers ──────────────────────────────────────────────────
    const formatMoney = (val) => {
        if (val === undefined || val === null || val === '') return '-';
        const num = Number(val);
        if (isNaN(num)) return val;
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const handleDownload = () => {
        alert('Download cover list initiated.');
    };

    return (
        <div className="d-flex flex-column w-100 h-100">
            {/* ══ Enterprise Toolbar ══ */}
            <div className="gtss-toolbar-strip">
                {/* ── Left: Module Title ── */}
                <div className="d-flex flex-column me-1" style={{ flexShrink: 0 }}>
                    <span className="gtss-page-title">Forward Cover</span>
                    <span className="gtss-page-subtitle">Forward Cover Records</span>
                </div>

                {/* ── Divider ── */}
                <div className="gtss-divider"></div>

                {/* ── Search ── */}
                <div className="input-group" style={{ width: '220px' }}>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search File / BG..."
                        aria-label="Search by File Name or BG Number"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <span className="input-group-text">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
                        </svg>
                    </span>
                </div>

                {/* ── Divider ── */}
                <div className="gtss-divider"></div>

                {/* ── Filters ── */}
                <div className="d-flex gap-2 align-items-center">
                    <select
                        className="form-select form-select-dark"
                        aria-label="Filter by Status"
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        style={{ minWidth: '110px' }}
                    >
                        <option value="">All Statuses</option>
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                    </select>
                </div>

                {/* ── Right Action Buttons ── */}
                <div className="d-flex align-items-center gap-2 ms-auto">
                    <button
                        className="btn btn-orange text-uppercase"
                        onClick={handleDownload}
                        aria-label="Download cover report"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16" className="me-1">
                            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/>
                            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z"/>
                        </svg>
                        Download
                    </button>
                </div>
            </div>

            {/* ── Table Container ── */}
            <div className="flex-grow-1 overflow-hidden d-flex flex-column gtss-view-container">
                <div className="border rounded-3 overflow-hidden bg-white d-flex flex-column flex-grow-1">
                    <div className="table-responsive flex-grow-1">
                        <table className="table table-hover align-middle mb-0 gtss-table text-nowrap">
                            <thead className="gtss-bg-primary text-white text-uppercase">
                                <tr>
                                    <th className="py-2 text-center" style={{ width: '80px' }}>S.No</th>
                                    <th className="py-2 text-start">File Name</th>
                                    <th className="py-2 text-start">BG Number</th>
                                    <th className="py-2 text-end">USD</th>
                                    <th className="py-2 text-center">Date</th>
                                    <th className="py-2 text-end">Rate</th>
                                    <th className="py-2 text-center">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedCovers.length > 0 ? (
                                    paginatedCovers.map((row, idx) => {
                                        const serialNo = (currentPage - 1) * ITEMS_PER_PAGE + idx + 1;
                                        return (
                                            <tr key={row.id}>
                                                <td className="text-muted fw-semibold text-center">{serialNo}</td>
                                                <td className="fw-semibold text-start">{row.fileId || '-'}</td>
                                                <td className="text-start">{row.bgNumber || '-'}</td>
                                                <td className="text-end fw-semibold text-dark">{formatMoney(row.usd)}</td>
                                                <td className="text-center">{row.date || '-'}</td>
                                                <td className="text-end">{row.rate || '-'}</td>
                                                <td className="text-center">
                                                    <span className={`badge px-3 py-1.5 rounded-pill text-uppercase fw-semibold ${row.status.toLowerCase() === 'active' ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                                                        {row.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="py-4 text-center text-muted">
                                            No forward cover records found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer strip: Total Amount and Pagination */}
                    <div className="border-top bg-white py-3 px-4 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                        <div className="d-flex align-items-center gap-2">
                            <span className="fw-bold text-secondary text-uppercase" style={{ fontSize: '12px', letterSpacing: '0.5px' }}>
                                Total Amount:
                            </span>
                            <span className="fs-6 fw-bold text-primary font-monospace px-3 py-1 bg-light border rounded-2" style={{ minWidth: '140px', display: 'inline-block', textAlign: 'right' }}>
                                {totalUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                            </span>
                        </div>

                        {totalPages >= 1 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForwardCover;